import type { SamplerRuntimeSample } from "../types";
import { getFactoryCycleSample } from "./factoryCycles";

const sampleCache = new Map<string, SamplerRuntimeSample>();

export function getCachedSample(id: string): SamplerRuntimeSample | undefined {
  const factory = getFactoryCycleSample(id);
  if (factory) {
    return factory;
  }
  return sampleCache.get(id);
}

export function putCachedSample(sample: SamplerRuntimeSample): void {
  if (sample.id.startsWith("factory/")) {
    return;
  }
  sampleCache.set(sample.id, sample);
}

export function removeCachedSample(id: string): void {
  sampleCache.delete(id);
}

export function clearSampleCache(): void {
  sampleCache.clear();
}

/** Deep-copy PCM so the buffer can be transferred to the worklet. */
export function cloneSampleForTransfer(sample: SamplerRuntimeSample): {
  sample: SamplerRuntimeSample;
  transfer: Transferable[];
} {
  const pcm = new Float32Array(sample.pcm.length);
  pcm.set(sample.pcm);
  return {
    sample: {
      id: sample.id,
      name: sample.name,
      sampleRate: sample.sampleRate,
      pcm,
      ...(sample.oneCycle ? { oneCycle: true } : {}),
      ...(sample.rootKey != null ? { rootKey: sample.rootKey } : {}),
    },
    transfer: [pcm.buffer],
  };
}

/** Mix multi-channel AudioBuffer to mono float PCM. */
export function audioBufferToMono(buffer: AudioBuffer): Float32Array {
  const frames = buffer.length;
  const channels = buffer.numberOfChannels;
  if (channels <= 1) {
    const out = new Float32Array(frames);
    out.set(buffer.getChannelData(0));
    return out;
  }
  const out = new Float32Array(frames);
  for (let c = 0; c < channels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < frames; i++) {
      out[i]! += data[i]!;
    }
  }
  const inv = 1 / channels;
  for (let i = 0; i < frames; i++) {
    out[i]! *= inv;
  }
  return out;
}

/** Average interleaved stereo (LRLR…) into mono float PCM. */
export function interleavedStereoToMono(stereo: Float32Array): Float32Array {
  const frames = Math.floor(stereo.length / 2);
  const out = new Float32Array(frames);
  for (let i = 0; i < frames; i++) {
    out[i] = ((stereo[i * 2] ?? 0) + (stereo[i * 2 + 1] ?? 0)) * 0.5;
  }
  return out;
}

export async function decodeAudioToSample(
  id: string,
  name: string,
  bytes: ArrayBuffer,
  audioContext: BaseAudioContext,
  options: { rootKey?: number } = {},
): Promise<SamplerRuntimeSample> {
  const buffer = await audioContext.decodeAudioData(bytes.slice(0));
  const pcm = audioBufferToMono(buffer);
  const sample: SamplerRuntimeSample = {
    id,
    name,
    sampleRate: buffer.sampleRate,
    pcm,
    rootKey: options.rootKey ?? 60,
  };
  putCachedSample(sample);
  return sample;
}
